import React, { useState } from 'react';
import { X, Plus, Minus, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useCart } from '../context/CartContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = '5562998485292';

const Cart = ({ open, onClose }) => {
  const { cart, updateQuantity, removeFromCart, getTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [customerData, setCustomerData] = useState({
    nome: '',
    whatsapp: '',
    endereco: ''
  });

  if (!open) return null;

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Carrinho vazio!');
      return;
    }

    // Validar dados do cliente
    if (!customerData.nome.trim()) {
      toast.error('Por favor, preencha seu nome');
      return;
    }
    if (!customerData.whatsapp.trim()) {
      toast.error('Por favor, preencha seu WhatsApp');
      return;
    }
    if (!customerData.endereco.trim()) {
      toast.error('Por favor, preencha seu endereço');
      return;
    }

    setLoading(true);

    try {
      // Preparar dados do pedido com informações do cliente
      const orderData = {
        items: cart.map(item => ({
          id: item.id,
          nome: item.nome,
          preco: item.preco,
          quantidade: item.quantidade
        })),
        total: getTotal(),
        customer: {
          nome: customerData.nome,
          whatsapp: customerData.whatsapp,
          endereco: customerData.endereco
        }
      };

      // Enviar webhook com TODOS os dados
      await axios.post(`${API}/webhook/order`, orderData);

      // Preparar mensagem WhatsApp APENAS com produtos (sem dados pessoais)
      let message = '*Novo Pedido - Margareth Bernardo*\n\n';
      cart.forEach(item => {
        message += `• ${item.nome}\n`;
        message += `  Quantidade: ${item.quantidade}\n`;
        message += `  Preço unitário: R$ ${item.preco.toFixed(2)}\n`;
        message += `  Subtotal: R$ ${(item.preco * item.quantidade).toFixed(2)}\n\n`;
      });
      message += `*Total: R$ ${getTotal().toFixed(2)}*`;

      // Redirecionar para WhatsApp (sem dados pessoais na mensagem)
      const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');

      // Limpar carrinho e formulário
      clearCart();
      setCustomerData({ nome: '', whatsapp: '', endereco: '' });
      setShowCheckoutForm(false);
      toast.success('Pedido enviado! Você será redirecionado para o WhatsApp.');
      onClose();
    } catch (error) {
      console.error('Erro ao finalizar pedido:', error);
      toast.error('Erro ao processar pedido. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
        data-testid="cart-overlay"
      />
      <div 
        className="fixed right-0 top-0 h-full w-full md:w-96 bg-white z-50 shadow-2xl overflow-y-auto"
        data-testid="cart-sidebar"
      >
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-2xl" style={{ fontFamily: 'Playfair Display, serif' }}>Carrinho</h2>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-gray-100 rounded-full"
            data-testid="close-cart-button"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4">
          {cart.length === 0 ? (
            <p className="text-center text-gray-500 py-8" data-testid="empty-cart-message">
              Seu carrinho está vazio
            </p>
          ) : (
            <>
              <div className="space-y-4 mb-6">
                {cart.map(item => (
                  <div 
                    key={item.id} 
                    className="flex gap-3 border-b pb-4"
                    data-testid={`cart-item-${item.id}`}
                  >
                    <img
                      src={item.url_imagem}
                      alt={item.nome}
                      className="w-20 h-20 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm mb-1">{item.nome}</h3>
                      <p className="text-sm" style={{ color: '#666' }}>R$ {item.preco.toFixed(2)}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantidade - 1)}
                          className="p-1 hover:bg-gray-100 rounded"
                          data-testid={`decrease-quantity-${item.id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="text-sm font-semibold" data-testid={`quantity-${item.id}`}>
                          {item.quantidade}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantidade + 1)}
                          className="p-1 hover:bg-gray-100 rounded"
                          data-testid={`increase-quantity-${item.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="ml-auto p-1 hover:bg-gray-100 rounded text-red-500"
                          data-testid={`remove-item-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 mb-4">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-semibold">Total:</span>
                  <span 
                    className="text-2xl font-bold" 
                    style={{ fontFamily: 'Playfair Display, serif' }}
                    data-testid="cart-total"
                  >
                    R$ {getTotal().toFixed(2)}
                  </span>
                </div>

                {!showCheckoutForm ? (
                  <Button
                    onClick={() => setShowCheckoutForm(true)}
                    className="w-full bg-black text-white hover:bg-gray-800"
                    data-testid="open-checkout-form-button"
                  >
                    Continuar para Checkout
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg mb-3">Informações de Entrega</h3>
                    
                    <div>
                      <Label htmlFor="customer-name">Nome Completo *</Label>
                      <Input
                        id="customer-name"
                        data-testid="customer-name-input"
                        placeholder="Seu nome completo"
                        value={customerData.nome}
                        onChange={(e) => setCustomerData({ ...customerData, nome: e.target.value })}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="customer-whatsapp">WhatsApp *</Label>
                      <Input
                        id="customer-whatsapp"
                        data-testid="customer-whatsapp-input"
                        placeholder="(62) 99999-9999"
                        value={customerData.whatsapp}
                        onChange={(e) => setCustomerData({ ...customerData, whatsapp: e.target.value })}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="customer-address">Endereço Completo *</Label>
                      <Textarea
                        id="customer-address"
                        data-testid="customer-address-input"
                        placeholder="Rua, número, bairro, cidade"
                        value={customerData.endereco}
                        onChange={(e) => setCustomerData({ ...customerData, endereco: e.target.value })}
                        rows={3}
                        required
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={() => setShowCheckoutForm(false)}
                        variant="outline"
                        className="flex-1"
                        data-testid="cancel-checkout-button"
                      >
                        Voltar
                      </Button>
                      <Button
                        onClick={handleCheckout}
                        disabled={loading}
                        className="flex-1 bg-black text-white hover:bg-gray-800"
                        data-testid="checkout-button"
                      >
                        {loading ? 'Processando...' : 'Finalizar Pedido'}
                      </Button>
                    </div>
                    <p className="text-xs text-center text-gray-500">
                      Você será redirecionado para o WhatsApp
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Cart;